import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { FileCheck, CheckCircle, XCircle, Eye, User, FileText, Camera, Home } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

export const AdminKYC = () => {
  const { adminApi } = useAuth();
  const [kycList, setKycList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKYC, setSelectedKYC] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchKYC();
  }, []);

  const fetchKYC = async () => {
    try {
      const res = await adminApi.get('/admin/kyc/pending');
      setKycList(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    setProcessing(true);
    try {
      await adminApi.put(`/admin/kyc/${userId}/approve`);
      toast.success('KYC approval submitted for supervisor review');
      setSelectedKYC(null);
      fetchKYC();
    } catch (error) {
      toast.error('Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (userId) => {
    setProcessing(true);
    try {
      await adminApi.put(`/admin/kyc/${userId}/reject`);
      toast.success('KYC rejection submitted for supervisor review');
      setSelectedKYC(null);
      fetchKYC();
    } catch (error) {
      toast.error('Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-afriperx-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-kyc">
      <div>
        <h1 className="font-syne text-2xl font-bold text-gray-900">KYC Approvals</h1>
        <p className="text-gray-600">{kycList.length} pending verifications</p>
      </div>

      {kycList.length === 0 ? (
        <Card className="bg-white border border-gray-200 p-12 text-center rounded-xl">
          <FileCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No pending KYC applications</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {kycList.map((kyc) => (
            <Card key={kyc.user_id} className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-gray-900 font-medium">{kyc.name}</p>
                  <p className="text-gray-500 text-sm">{kyc.mobile}</p>
                </div>
              </div>
              
              <div className="mb-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  kyc.kyc_status === 'submitted' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {kyc.kyc_status}
                </span>
                <span className="text-gray-500 text-xs ml-2">
                  {kyc.documents?.length || 0} documents
                </span>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={() => setSelectedKYC(kyc)}
                  variant="outline"
                  className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                  data-testid={`view-kyc-${kyc.user_id}`}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Review
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* KYC Review Dialog */}
      <Dialog open={!!selectedKYC} onOpenChange={() => setSelectedKYC(null)}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-gray-900 font-syne">KYC Review</DialogTitle>
          </DialogHeader>
          
          {selectedKYC && (
            <div className="space-y-6 pt-4">
              {/* User Info */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-14 h-14 rounded-full bg-afriperx-green/10 flex items-center justify-center">
                  <span className="text-afriperx-gold font-bold text-xl">
                    {selectedKYC.name?.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="text-gray-900 font-bold text-lg">{selectedKYC.name}</p>
                  <p className="text-gray-500">{selectedKYC.mobile}</p>
                </div>
              </div>

              {/* Documents */}
              <div className="grid grid-cols-3 gap-4">
                {['ID Document', 'Proof of Address', 'Selfie'].map((docType, idx) => {
                  const icons = [FileText, Home, Camera];
                  const IconComp = icons[idx];
                  const doc = selectedKYC.documents?.find(d => 
                    d.type === docType.toLowerCase().replace(/ /g, '_')
                  );
                  return (
                    <div key={idx} className="space-y-2">
                      <div className="aspect-square bg-gray-50 border border-gray-200 rounded-xl flex flex-col items-center justify-center p-4">
                        <IconComp className="w-10 h-10 text-gray-400 mb-2" />
                        <p className="text-gray-600 text-xs text-center">{docType}</p>
                      </div>
                      <p className={`text-xs text-center ${doc ? 'text-green-600' : 'text-gray-400'}`}>
                        {doc ? 'Uploaded' : 'Not uploaded'}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={() => handleReject(selectedKYC.user_id)}
                  disabled={processing}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="reject-kyc-btn"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={() => handleApprove(selectedKYC.user_id)}
                  disabled={processing}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  data-testid="approve-kyc-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
